import React, { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, Unlock } from "lucide-react";
import { SideBarContentHeader } from "@/common/ui/SideBar";
import Secure from "@/secure/payload";
import { toastErrorMessage, toastSuccessMessage } from "@/utils/helper/myToast";
import { Button } from "@/components/ui/button";
import { Row } from "@/common/css";
import { FieldErrorMessage } from "@/common/ui/Text";

const QAPayload = () => {
  const [input, setInput] = useState("");
  const [encryptedData, setEncryptedData] = useState("");
  const [isInValidObj, setIsInValidObj] = useState(false);
  const [isCopied, setIsCopied] = useState({ input: false, encrypted: false, decrypted: false });

  const handleEncrypt = () => {
    if (isInValidObj) setIsInValidObj(false);
    let encryptedString = "";

    try {
      const data = JSON.parse(input);
      const encrypted = Secure.payloadEncryption(data);
      encryptedString = JSON.stringify(encrypted, null, 2);
    } catch (err) {
      console.log(err);
      encryptedString = Secure.payloadEncryptionValue(input);
      setIsInValidObj(true);
    }
    setEncryptedData(encryptedString);
    handleCopy(encryptedString, "encrypted");
  };

  const handleDecrypt = () => {
    if (isInValidObj) setIsInValidObj(false);
    let decryptedString: any = "";

    try {
      const data = JSON.parse(input);
      const decrypted = Secure.payloadDecryption(data);
      decryptedString = JSON.stringify(decrypted, null, 2);
    } catch (err) {
      console.log(err);
      setIsInValidObj(true);
      decryptedString = Secure.payloadDecryptionValue(input);
    }
    setEncryptedData(decryptedString);
    handleCopy(decryptedString, "decrypted");
  };

  const handleCopy = (text: string, field: "input" | "encrypted" | "decrypted") => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setIsCopied({ ...isCopied, [field]: true });
        toastSuccessMessage(`${field} data copied to clipboard!`);
        setTimeout(() => setIsCopied({ ...isCopied, [field]: false }), 2000);
      })
      .catch(() => {
        toastErrorMessage(`Failed to copy ${field} data.`);
      });
  };

  const renderTextArea = (
    value: string,
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void,
    onCopy: () => void,
    isCopiedState: boolean,
    placeholder: string
  ) => (
    <div className="relative">
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full p-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-40"
      />
      <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className="absolute right-2 top-2" onClick={onCopy}>
        {isCopiedState ? <Check className="text-green-500" size={20} /> : <Copy className="text-gray-500" size={20} />}
      </motion.button>
    </div>
  );

  return (
    <div className="px-4">
      <SideBarContentHeader heading="Payload Encryption And Decryption" />

      <div className="mt-6 space-y-6">
        <section>
          <h2 className="text-xl font-semibold mb-4">Input JSON</h2>
          {renderTextArea(
            input,
            (e) => setInput(e.target.value),
            () => handleCopy(input, "input"),
            isCopied.input,
            "Enter JSON object here"
          )}
          {isInValidObj && <FieldErrorMessage text="Invalid Object" />}
          <Row className=" gap-x-2">
            <Button variant="outline" className="text-13 text-text-primary mt-5 " onClick={handleEncrypt}>
              <Unlock size={20} className="mr-2" /> Encrypt
            </Button>
            <Button variant="outline" className="text-13 text-text-primary mt-5 " onClick={handleDecrypt}>
              <Unlock size={20} className="mr-2" /> Decrypt
            </Button>
          </Row>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-4">Processed Data</h2>
          {renderTextArea(
            encryptedData,
            (e) => setEncryptedData(e.target.value),
            () => handleCopy(encryptedData, "encrypted"),
            isCopied.encrypted,
            "Encrypted data will appear here"
          )}
        </section>
      </div>
    </div>
  );
};

export default QAPayload;
